import { useEffect, useState } from 'react';
import type { CrmCampaignRef, CrmContact, CrmTag } from '../../types/crm';

interface CrmContactDrawerProps {
  isOpen: boolean;
  contact: CrmContact | null;
  availableTags: CrmTag[];
  campaigns: CrmCampaignRef[];
  onClose: () => void;
  onSaveContact: (payload: Record<string, unknown>, contactId?: string) => Promise<void>;
  onUpdateStage: (contactId: string, stage: string) => Promise<void>;
  onCreateInteraction: (contactId: string, payload: Record<string, unknown>) => Promise<void>;
  onCreateTask: (contactId: string, payload: Record<string, unknown>) => Promise<void>;
  onAssignTag: (contactId: string, tagId: string) => Promise<void>;
  onRemoveTag: (contactId: string, tagId: string) => Promise<void>;
  onLinkCampaign: (contactId: string, campaignId: string) => Promise<void>;
}

const STAGE_OPTIONS = [
  { value: 'NOVO_CONTATO', label: 'Novo contato' },
  { value: 'IDENTIFICADO', label: 'Identificado' },
  { value: 'CONTATO_FEITO', label: 'Contato feito' },
  { value: 'INTERESSADO', label: 'Interessado' },
  { value: 'EM_ACOMPANHAMENTO', label: 'Em acompanhamento' },
  { value: 'CONVERTIDO', label: 'Convertido' },
  { value: 'SEM_RESPOSTA', label: 'Sem resposta' },
  { value: 'SEM_INTERESSE', label: 'Sem interesse' },
  { value: 'INATIVO', label: 'Inativo' }
];

const TASK_OPTIONS = ['Ligar', 'Reenviar mensagem', 'Agendar retorno', 'Fazer acompanhamento', 'Enviar proposta', 'Confirmar presença', 'Visita', 'Reunião'];
const INTERACTION_TYPES = ['WHATSAPP_ENVIADO', 'EMAIL_ENVIADO', 'RESPOSTA_RECEBIDA', 'OBSERVACAO', 'ALTERACAO_STATUS'];

const formatDateTime = (value?: string | null) => {
  if (!value) return 'Sem registro';
  return new Date(value).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

export function CrmContactDrawer({
  isOpen,
  contact,
  availableTags,
  campaigns,
  onClose,
  onSaveContact,
  onUpdateStage,
  onCreateInteraction,
  onCreateTask,
  onAssignTag,
  onRemoveTag,
  onLinkCampaign
}: CrmContactDrawerProps) {
  const [form, setForm] = useState<Record<string, unknown>>({
    nome: '',
    telefone: '',
    email: '',
    cidade: '',
    bairro: '',
    origemContato: '',
    interessePrincipal: '',
    tipoCampanha: 'RELACIONAMENTO',
    statusJornada: 'NOVO_CONTATO',
    nivelInteresse: '',
    responsavel: '',
    observacoes: '',
    consentimentoMensagens: true
  });
  const [selectedTagId, setSelectedTagId] = useState('');
  const [selectedCampaignId, setSelectedCampaignId] = useState('');
  const [taskForm, setTaskForm] = useState({ titulo: TASK_OPTIONS[0], descricao: '', dataAgendada: '' });
  const [interactionForm, setInteractionForm] = useState({ tipo: INTERACTION_TYPES[0], canal: 'WHATSAPP', mensagem: '' });

  useEffect(() => {
    if (contact) {
      setForm({
        nome: contact.nome || '',
        telefone: contact.telefone || '',
        email: contact.email || '',
        cidade: contact.cidade || '',
        bairro: contact.bairro || '',
        origemContato: contact.origemContato || '',
        interessePrincipal: contact.interessePrincipal || '',
        tipoCampanha: contact.tipoCampanha || 'RELACIONAMENTO',
        statusJornada: contact.statusJornada || 'NOVO_CONTATO',
        nivelInteresse: contact.nivelInteresse || '',
        responsavel: contact.responsavel || '',
        observacoes: contact.observacoes || '',
        consentimentoMensagens: contact.consentimentoMensagens ?? true,
        proximaAcao: contact.proximaAcao ? String(contact.proximaAcao).slice(0, 16) : ''
      });
    }
  }, [contact]);

  if (!isOpen) return null;

  const isNew = !contact?.id;

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-slate-950/30 backdrop-blur-sm">
      <div className="h-full w-full max-w-2xl overflow-y-auto border-l border-slate-200 bg-white shadow-2xl">
        <div className="sticky top-0 z-10 border-b border-slate-200 bg-white/95 px-6 py-5 backdrop-blur">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">CRM</p>
              <h2 className="mt-1 text-2xl font-semibold text-slate-950">
                {isNew ? 'Novo contato' : contact?.nome}
              </h2>
              <p className="mt-1 text-sm text-slate-500">
                {isNew ? 'Cadastre um contato e inicie a jornada.' : 'Detalhes, tarefas, interacoes e campanhas vinculadas.'}
              </p>
            </div>
            <button onClick={onClose} className="rounded-2xl border border-slate-200 px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50">
              Fechar
            </button>
          </div>
        </div>

        <div className="space-y-8 p-6">
          <section className="rounded-3xl border border-slate-200 bg-slate-50 p-5">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {[
                ['nome', 'Nome'],
                ['telefone', 'Telefone'],
                ['email', 'Email'],
                ['cidade', 'Cidade'],
                ['bairro', 'Bairro'],
                ['origemContato', 'Origem do contato'],
                ['interessePrincipal', 'Interesse principal'],
                ['responsavel', 'Responsavel']
              ].map(([key, label]) => (
                <label key={key} className="block">
                  <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</span>
                  <input
                    value={String(form[key] || '')}
                    onChange={(event) => setForm((prev) => ({ ...prev, [key]: event.target.value }))}
                    className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-slate-400"
                  />
                </label>
              ))}

              <label className="block">
                <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Tipo de campanha</span>
                <select
                  value={String(form.tipoCampanha || 'RELACIONAMENTO')}
                  onChange={(event) => setForm((prev) => ({ ...prev, tipoCampanha: event.target.value }))}
                  className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-slate-400"
                >
                  <option value="COMERCIAL">Comercial</option>
                  <option value="POLITICA">Politica</option>
                  <option value="INSTITUCIONAL">Institucional</option>
                  <option value="RELACIONAMENTO">Relacionamento</option>
                </select>
              </label>

              <label className="block">
                <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Nivel de interesse</span>
                <input
                  value={String(form.nivelInteresse || '')}
                  onChange={(event) => setForm((prev) => ({ ...prev, nivelInteresse: event.target.value }))}
                  className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-slate-400"
                />
              </label>

              <label className="block md:col-span-2">
                <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Observacoes</span>
                <textarea
                  rows={4}
                  value={String(form.observacoes || '')}
                  onChange={(event) => setForm((prev) => ({ ...prev, observacoes: event.target.value }))}
                  className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-slate-400"
                />
              </label>
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-3">
              {!isNew && (
                <select
                  value={String(form.statusJornada || contact?.statusJornada || 'NOVO_CONTATO')}
                  onChange={async (event) => {
                    const stage = event.target.value;
                    setForm((prev) => ({ ...prev, statusJornada: stage }));
                    if (contact?.id) {
                      await onUpdateStage(contact.id, stage);
                    }
                  }}
                  className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900"
                >
                  {STAGE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              )}

              <button
                onClick={() => onSaveContact(form, contact?.id)}
                className="rounded-2xl bg-slate-900 px-4 py-3 text-sm font-semibold text-white hover:bg-slate-800"
              >
                {isNew ? 'Criar contato' : 'Salvar contato'}
              </button>
            </div>
          </section>

          {!isNew && contact ? (
            <>
              <section className="grid gap-4 lg:grid-cols-2">
                <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-slate-900">Tags</h3>
                    <div className="flex gap-2">
                      <select
                        value={selectedTagId}
                        onChange={(event) => setSelectedTagId(event.target.value)}
                        className="rounded-xl border border-slate-200 px-3 py-2 text-sm"
                      >
                        <option value="">Selecionar tag</option>
                        {availableTags.map((tag) => (
                          <option key={tag.id} value={tag.id}>{tag.name}</option>
                        ))}
                      </select>
                      <button
                        onClick={() => selectedTagId && onAssignTag(contact.id, selectedTagId)}
                        className="rounded-xl border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                      >
                        Adicionar
                      </button>
                    </div>
                  </div>

                  <div className="mt-4 flex flex-wrap gap-2">
                    {(contact.tagItems || []).map((tag) => (
                      <button
                        key={tag.id}
                        onClick={() => onRemoveTag(contact.id, tag.id)}
                        className="rounded-full px-3 py-1 text-xs font-semibold text-white"
                        style={{ backgroundColor: tag.color }}
                      >
                        {tag.name}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-slate-900">Campanhas</h3>
                    <div className="flex gap-2">
                      <select
                        value={selectedCampaignId}
                        onChange={(event) => setSelectedCampaignId(event.target.value)}
                        className="rounded-xl border border-slate-200 px-3 py-2 text-sm"
                      >
                        <option value="">Selecionar campanha</option>
                        {campaigns.map((campaign) => (
                          <option key={campaign.id} value={campaign.id}>{campaign.nome}</option>
                        ))}
                      </select>
                      <button
                        onClick={() => selectedCampaignId && onLinkCampaign(contact.id, selectedCampaignId)}
                        className="rounded-xl border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                      >
                        Vincular
                      </button>
                    </div>
                  </div>

                  <div className="mt-4 space-y-2 text-sm text-slate-600">
                    {(contact.linkedCampaigns || []).length > 0 ? (
                      (contact.linkedCampaigns || []).map((item: any) => (
                        <div key={item.id} className="rounded-2xl border border-slate-100 bg-slate-50 px-3 py-2">
                          Campanha vinculada em {formatDateTime(item.addedAt)}
                        </div>
                      ))
                    ) : (
                      <p className="text-slate-400">Nenhuma campanha vinculada manualmente.</p>
                    )}
                  </div>
                </div>
              </section>

              <section className="grid gap-4 lg:grid-cols-2">
                <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
                  <h3 className="text-sm font-semibold text-slate-900">Registrar interacao</h3>
                  <div className="mt-4 space-y-3">
                    <select
                      value={interactionForm.tipo}
                      onChange={(event) => setInteractionForm((prev) => ({ ...prev, tipo: event.target.value }))}
                      className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm"
                    >
                      {INTERACTION_TYPES.map((option) => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                    <select
                      value={interactionForm.canal}
                      onChange={(event) => setInteractionForm((prev) => ({ ...prev, canal: event.target.value }))}
                      className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm"
                    >
                      <option value="WHATSAPP">WhatsApp</option>
                      <option value="EMAIL">E-mail</option>
                      <option value="SISTEMA">Sistema</option>
                    </select>
                    <textarea
                      rows={3}
                      value={interactionForm.mensagem}
                      onChange={(event) => setInteractionForm((prev) => ({ ...prev, mensagem: event.target.value }))}
                      className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm"
                      placeholder="Descreva a interacao"
                    />
                    <button
                      onClick={() => onCreateInteraction(contact.id, interactionForm)}
                      className="rounded-2xl bg-emerald-600 px-4 py-3 text-sm font-semibold text-white hover:bg-emerald-500"
                    >
                      Registrar WhatsApp / E-mail
                    </button>
                  </div>
                </div>

                <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
                  <h3 className="text-sm font-semibold text-slate-900">Criar tarefa</h3>
                  <div className="mt-4 space-y-3">
                    <select
                      value={taskForm.titulo}
                      onChange={(event) => setTaskForm((prev) => ({ ...prev, titulo: event.target.value }))}
                      className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm"
                    >
                      {TASK_OPTIONS.map((option) => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                    <input
                      type="datetime-local"
                      value={taskForm.dataAgendada}
                      onChange={(event) => setTaskForm((prev) => ({ ...prev, dataAgendada: event.target.value }))}
                      className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm"
                    />
                    <textarea
                      rows={3}
                      value={taskForm.descricao}
                      onChange={(event) => setTaskForm((prev) => ({ ...prev, descricao: event.target.value }))}
                      className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm"
                      placeholder="Detalhes da tarefa"
                    />
                    <button
                      onClick={() => onCreateTask(contact.id, taskForm)}
                      className="rounded-2xl bg-amber-500 px-4 py-3 text-sm font-semibold text-slate-950 hover:bg-amber-400"
                    >
                      Criar tarefa
                    </button>
                  </div>
                </div>
              </section>

              <section className="grid gap-4 lg:grid-cols-2">
                <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
                  <h3 className="text-sm font-semibold text-slate-900">Tarefas pendentes</h3>
                  <div className="mt-4 space-y-3">
                    {(contact.tasks || []).length > 0 ? (
                      (contact.tasks || []).map((task) => (
                        <div key={task.id} className="rounded-2xl border border-slate-100 bg-slate-50 p-3">
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <p className="text-sm font-semibold text-slate-900">{task.titulo}</p>
                              <p className="text-xs text-slate-500">{task.descricao || 'Sem descricao'}</p>
                            </div>
                            <span className="rounded-full bg-white px-2 py-1 text-[11px] font-semibold text-slate-600">
                              {task.status}
                            </span>
                          </div>
                          <p className="mt-2 text-xs text-slate-500">Agendada para {formatDateTime(task.dataAgendada)}</p>
                        </div>
                      ))
                    ) : (
                      <p className="text-sm text-slate-400">Nenhuma tarefa pendente.</p>
                    )}
                  </div>
                </div>

                <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
                  <h3 className="text-sm font-semibold text-slate-900">Timeline</h3>
                  <div className="mt-4 space-y-3">
                    {(contact.interactions || []).length > 0 ? (
                      (contact.interactions || []).slice(0, 20).map((interaction) => (
                        <div key={interaction.id} className="rounded-2xl border border-slate-100 bg-slate-50 p-3">
                          <div className="flex items-center justify-between gap-3">
                            <p className="text-sm font-semibold text-slate-900">{interaction.tipo}</p>
                            <span className="text-xs text-slate-500">{formatDateTime(interaction.criadoEm)}</span>
                          </div>
                          <p className="mt-1 text-xs uppercase tracking-wide text-slate-400">{interaction.canal || 'SISTEMA'}</p>
                          <p className="mt-2 text-sm text-slate-600">{interaction.mensagem || 'Sem mensagem registrada.'}</p>
                        </div>
                      ))
                    ) : (
                      <p className="text-sm text-slate-400">Nenhuma interacao registrada.</p>
                    )}
                  </div>
                </div>
              </section>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}
